/**
 * Download Service
 * Handles file downloads including ZIP archives for various resource types
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Get list of supported downloadable resource types
 * @returns {Promise<Object>} - List of supported resources
 */
export const getSupportedResources = async () => {
  try {
    const response = await fetch(`${API_URL}/download/resources`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to get supported resources: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting supported resources:', error);
    throw error;
  }
};

/**
 * List files available for download
 * @param {string} resourceType - Type of resource (wireframes, designs, etc.)
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} - List of files
 */
export const listDownloadableFiles = async (resourceType, projectId) => {
  try {
    const response = await fetch(
      `${API_URL}/download/${resourceType}/${projectId}/files`,
      {
        headers: getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to list files: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error listing ${resourceType} files:`, error);
    throw error;
  }
};

/**
 * Download files as ZIP
 * @param {string} resourceType - Type of resource (wireframes, designs, etc.)
 * @param {string} projectId - Project ID
 * @param {string} customFilename - Optional custom filename for the ZIP
 * @returns {Promise<void>}
 */
export const downloadAsZip = async (resourceType, projectId, customFilename = null) => {
  try {
    const response = await fetch(
      `${API_URL}/download/${resourceType}/${projectId}/zip`,
      {
        headers: getAuthHeaders()
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Download failed: ${response.status}`);
    }

    // Get filename from Content-Disposition header or use custom/default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = customFilename;

    if (!filename && contentDisposition) {
      // Match filename with or without quotes, non-greedy to avoid capturing trailing quote
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"|filename=([^;\s]+)/);
      if (filenameMatch) {
        filename = filenameMatch[1] || filenameMatch[2];
      }
    }

    if (!filename) {
      filename = `${resourceType}_${projectId}.zip`;
    }

    // Create blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  } catch (error) {
    console.error(`Error downloading ${resourceType} ZIP:`, error);
    throw error;
  }
};

export default {
  getSupportedResources,
  listDownloadableFiles,
  downloadAsZip
};
